import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function Login() {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Correct password
  const CORRECT_PASSWORD = 'MIS@2108';

  // Check if already logged in
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (isLoggedIn === 'true') {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    // Simulate async login (add small delay for better UX)
    setTimeout(() => {
      if (password === CORRECT_PASSWORD) {
        localStorage.setItem('adminLoggedIn', 'true');
        localStorage.setItem('adminLoginTime', new Date().toISOString());
        navigate('/admin/dashboard');
      } else {
        setError('Incorrect password. Please try again.');
        setPassword('');
        setLoading(false);
      }
    }, 500);
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-icon">🔐</div>
          <h2>Admin Login</h2>
          <p>Enter your credentials to access the dashboard</p>
        </div>
        
        <form onSubmit={handleLogin} className="login-form">
          <div className="input-group">
            <label>Password</label>
            <div className="password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                disabled={loading}
              />
              <button 
                type="button" 
                className="toggle-password"
                onClick={toggleShowPassword}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          
          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                Verifying...
              </>
            ) : (
              'Login to Dashboard'
            )}
          </button>
          
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
        </form>
        
        <div className="login-footer">
          <p>Secure Admin Access Only</p>
          <small>Authorized personnel only</small>
        </div>
      </div>

      <style>{`
        .login-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
        }
        
        /* Background decoration */
        .login-container::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: moveBackground 20s linear infinite;
        }
        
        @keyframes moveBackground {
          from {
            transform: translate(0, 0);
          }
          to {
            transform: translate(50px, 50px);
          }
        }
        
        .login-card {
          background: white;
          border-radius: 24px;
          padding: 45px 40px;
          width: 100%;
          max-width: 450px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          position: relative;
          z-index: 1;
          animation: slideIn 0.5s ease;
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .login-header {
          text-align: center;
          margin-bottom: 35px;
        }
        
        .login-icon {
          font-size: 60px;
          margin-bottom: 15px;
        }
        
        .login-header h2 {
          font-size: 28px;
          color: #1a3a6b;
          margin-bottom: 8px;
          font-weight: 700;
        }
        
        .login-header p {
          color: #64748b;
          font-size: 14px;
        }
        
        .login-form {
          margin-bottom: 25px;
        }
        
        .input-group {
          margin-bottom: 25px;
        }
        
        .input-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #334155;
          font-size: 14px;
        }
        
        .password-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .password-wrapper input {
          width: 100%;
          padding: 14px 45px 14px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 16px;
          transition: all 0.3s ease;
          background: #f8fafc;
        }
        
        .password-wrapper input:focus {
          outline: none;
          border-color: #1a3a6b;
          background: white;
          box-shadow: 0 0 0 3px rgba(26, 58, 107, 0.1);
        }
        
        .password-wrapper input:disabled {
          background: #f1f5f9;
          cursor: not-allowed;
        }
        
        .toggle-password {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 20px;
          padding: 0;
          width: auto;
          border-radius: 0;
          transition: transform 0.2s;
        }
        
        .toggle-password:hover {
          transform: scale(1.1);
        }
        
        .login-btn {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #1a3a6b, #2d5aa0);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }
        
        .login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
          background: linear-gradient(135deg, #2d5aa0, #1a3a6b);
        }
        
        .login-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .spinner {
          display: inline-block;
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .error-message {
          margin-top: 20px;
          padding: 12px;
          background: #fef2f2;
          border-left: 4px solid #ef4444;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: #dc2626;
          font-size: 14px;
          font-weight: 500;
        }
        
        .error-icon {
          font-size: 18px;
        }
        
        .login-footer {
          text-align: center;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }
        
        .login-footer p {
          color: #64748b;
          font-size: 12px;
          margin-bottom: 4px;
        }
        
        .login-footer small {
          color: #94a3b8;
          font-size: 11px;
        }
        
        @media (max-width: 480px) {
          .login-card {
            margin: 20px;
            padding: 35px 25px;
          }
          
          .login-header h2 {
            font-size: 24px;
          }
          
          .login-icon {
            font-size: 50px;
          }
          
          .password-wrapper input {
            padding: 12px 45px 12px 14px;
            font-size: 14px;
          }
          
          .login-btn {
            padding: 12px;
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
}

export default Login;